import type { NoteColor, NoteDto, NoteType, NoteView } from '@keep/shared';
import type { QueryClient } from '@tanstack/react-query';

/** Filtre d'une liste de notes ; c'est aussi la clé de cache de la liste. */
export interface NoteFilter {
  view: NoteView;
  labelId?: string;
  q?: string;
  color?: NoteColor;
  type?: NoteType;
  hasImages?: boolean;
}

export const noteKeys = {
  all: ['notes'] as const,
  lists: ['notes', 'list'] as const,
  list: (filter: NoteFilter) => ['notes', 'list', filter] as const,
  detail: (id: string) => ['notes', 'detail', id] as const,
};

/**
 * Réplique côté client des règles de filtrage de l'API.
 * Permet les mises à jour optimistes : une note archivée quitte instantanément la vue « Notes ».
 */
export function matchesFilter(note: NoteDto, filter: NoteFilter): boolean {
  const trashed = note.trashedAt !== null;
  switch (filter.view) {
    case 'notes':
      if (trashed || note.archived) return false;
      break;
    case 'archive':
      if (trashed || !note.archived) return false;
      break;
    case 'reminders':
      if (trashed || !note.reminderAt) return false;
      break;
    case 'trash':
      if (!trashed) return false;
      break;
    case 'all':
      if (trashed) return false;
      break;
  }
  if (filter.labelId && !note.labelIds.includes(filter.labelId)) return false;
  if (filter.color && note.color !== filter.color) return false;
  if (filter.type && note.type !== filter.type) return false;
  if (filter.hasImages !== undefined && note.attachments.length > 0 !== filter.hasImages)
    return false;
  if (filter.q) {
    const q = filter.q.toLocaleLowerCase();
    const haystack = [note.title, note.content, ...note.items.map((i) => i.text)];
    if (!haystack.some((text) => text.toLocaleLowerCase().includes(q))) return false;
  }
  return true;
}

/** Même ordre que l'API pour chaque vue. */
export function compareNotes(view: NoteView) {
  return (a: NoteDto, b: NoteDto): number => {
    if (view === 'reminders') {
      const diff = (a.reminderAt ?? '').localeCompare(b.reminderAt ?? '');
      return diff !== 0 ? diff : b.position - a.position;
    }
    if (view === 'trash') return (b.trashedAt ?? '').localeCompare(a.trashedAt ?? '');
    if (a.pinned !== b.pinned) return a.pinned ? -1 : 1;
    return b.position - a.position;
  };
}

export function upsertInList(list: NoteDto[], note: NoteDto, filter: NoteFilter): NoteDto[] {
  const without = list.filter((n) => n.id !== note.id);
  if (!matchesFilter(note, filter)) return without.length === list.length ? list : without;
  return [...without, note].sort(compareNotes(filter.view));
}

/** Répercute une note (optimiste ou renvoyée par l'API) dans toutes les listes en cache. */
export function applyNote(queryClient: QueryClient, note: NoteDto) {
  for (const [key, list] of queryClient.getQueriesData<NoteDto[]>({ queryKey: noteKeys.lists })) {
    if (!list) continue;
    const filter = key[2] as NoteFilter;
    queryClient.setQueryData(key, upsertInList(list, note, filter));
  }
  queryClient.setQueryData(noteKeys.detail(note.id), note);
}

export function removeNote(queryClient: QueryClient, id: string) {
  for (const [key, list] of queryClient.getQueriesData<NoteDto[]>({ queryKey: noteKeys.lists })) {
    if (list?.some((n) => n.id === id)) {
      queryClient.setQueryData(
        key,
        list.filter((n) => n.id !== id),
      );
    }
  }
  queryClient.removeQueries({ queryKey: noteKeys.detail(id) });
}

/** Cherche la version la plus récente d'une note dans le cache (détail ou listes). */
export function findCachedNote(queryClient: QueryClient, id: string): NoteDto | undefined {
  const detail = queryClient.getQueryData<NoteDto>(noteKeys.detail(id));
  if (detail) return detail;
  for (const [, list] of queryClient.getQueriesData<NoteDto[]>({ queryKey: noteKeys.lists })) {
    const found = list?.find((n) => n.id === id);
    if (found) return found;
  }
  return undefined;
}

export type CacheSnapshot = Array<[readonly unknown[], unknown]>;

/** Photographie des notes en cache, pour annuler une mise à jour optimiste en cas d'échec. */
export function snapshotNotes(queryClient: QueryClient): CacheSnapshot {
  return queryClient.getQueriesData({ queryKey: noteKeys.all });
}

export function restoreSnapshot(queryClient: QueryClient, snapshot: CacheSnapshot) {
  for (const [key, data] of snapshot) queryClient.setQueryData(key, data);
}
